/**
 * Example Angular Component - Demonstrates Route Migration
 *
 * This component shows how to create an Angular view that can be
 * accessed via UI-Router in the hybrid application.
 *
 * Key points:
 * 1. This is a regular Angular component
 * 2. It's registered as a UI-Router state in the routing module
 * 3. It can be a child of an AngularJS parent state
 * 4. It can use Angular services and features
 * 5. It can also use upgraded AngularJS services if needed
 * 6. It can use Lit web components (framework-agnostic)
 *
 * NOTE: When a component is downgraded for use in AngularJS templates,
 * be careful with UI-Router service injection. Use UIRouter from @uirouter/core
 * which is properly provided by UIRouterUpgradeModule.
 */
import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';

@Component({
  selector: 'app-example',
  standalone: true,
  imports: [SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="p-5 ng-example-view">
      <!-- Bootstrap 3 panel (existing style) -->
      <div class="panel panel-default mb-6">
        <div class="panel-heading">
          <h3 class="panel-title">
            <i class="fa fa-angular text-[#dd0031] mr-2"></i>
            Angular Example View
          </h3>
        </div>
        <div class="panel-body">
          <app-alert type="success">
            This view is rendered by Angular, not AngularJS!
          </app-alert>

          <h4 class="text-lg font-semibold mb-3">Migration Status</h4>
          <ul class="list-group">
            <li class="list-group-item">
              <i class="fa fa-check text-success mr-2"></i>
              Angular 18 is running
            </li>
            <li class="list-group-item">
              <i class="fa fa-check text-success mr-2"></i>
              UI-Router hybrid is working
            </li>
            <li class="list-group-item">
              <i class="fa fa-check text-success mr-2"></i>
              Tailwind CSS is available
            </li>
            <li class="list-group-item">
              <i class="fa fa-check text-success mr-2"></i>
              Lit web components working in Angular
            </li>
            <li class="list-group-item">
              <i class="fa fa-check text-success mr-2"></i>
              AngularJS directives upgraded for use in Angular
            </li>
          </ul>
        </div>
      </div>

      <!-- New Tailwind-styled section -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <h4 class="text-xl font-bold text-primary mb-4">
          <i class="fa fa-magic mr-2 text-app-blue"></i>
          Tailwind CSS Examples
        </h4>
        <p class="text-secondary mb-4">
          These elements use Tailwind CSS v4 utility classes:
        </p>

        <!-- Tailwind buttons -->
        <div class="flex flex-wrap gap-3 mb-6">
          <button class="bg-primary text-white px-4 py-2 rounded hover:opacity-80 transition">
            Primary Button
          </button>
          <button class="bg-success text-white px-4 py-2 rounded hover:opacity-80 transition">
            Success Button
          </button>
          <button class="bg-danger text-white px-4 py-2 rounded hover:opacity-80 transition">
            Danger Button
          </button>
          <button class="bg-app-blue text-white px-4 py-2 rounded hover:opacity-80 transition">
            App Blue Button
          </button>
        </div>

        <!-- Tailwind grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="bg-dashboard-gray-light p-4 rounded-lg border border-dashboard-gray-tertiary">
            <h5 class="font-semibold mb-2">Card 1</h5>
            <p class="text-sm text-tertiary">Using Tailwind grid and custom colors</p>
          </div>
          <div class="bg-dashboard-gray-light p-4 rounded-lg border border-dashboard-gray-tertiary">
            <h5 class="font-semibold mb-2">Card 2</h5>
            <p class="text-sm text-tertiary">Works alongside Bootstrap 3</p>
          </div>
          <div class="bg-dashboard-gray-light p-4 rounded-lg border border-dashboard-gray-tertiary">
            <h5 class="font-semibold mb-2">Card 3</h5>
            <p class="text-sm text-tertiary">No conflicts with existing styles</p>
          </div>
        </div>
      </div>

      <!-- Upgraded AngularJS Component Example -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <h4 class="text-xl font-bold mb-4">
          <i class="fa fa-cogs mr-2 text-success"></i>
          AngularJS Component (Upgraded)
        </h4>
        <p class="text-secondary mb-4">
          This is the migrated <strong>Angular AlertComponent</strong> used directly via its <code class="bg-gray-100 px-1 rounded">app-alert</code> selector:
        </p>
        <div class="space-y-4">
          <app-alert [type]="alertType">
            This alert is rendered by the native Angular AlertComponent!
          </app-alert>
          <div class="flex gap-2 mt-4">
            <button
              *ngFor="let t of alertTypes"
              class="px-3 py-1 rounded text-sm"
              [class.bg-primary]="alertType === t"
              [class.text-white]="alertType === t"
              [class.bg-gray-200]="alertType !== t"
              (click)="setAlertType(t)">
              {{ t }}
            </button>
          </div>
          <p class="text-sm text-tertiary mt-2">
            <code class="bg-gray-100 px-1 rounded">&lt;app-alert&gt;</code> is the native Angular component (also downgraded as <code class="bg-gray-100 px-1 rounded">acme-alert</code> for AngularJS)
          </p>
        </div>
      </div>

      <!-- Lit Web Component Examples -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <h4 class="text-xl font-bold mb-4">Lit Web Components</h4>
        <p class="text-secondary mb-4">These cards are rendered by Lit, not Angular:</p>

        <h5 class="font-semibold mb-2">Original Lit Component (Shadow DOM)</h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <example-card cardTitle="Framework Agnostic" variant="info">
            This Lit component uses Shadow DOM with scoped styles.
          </example-card>
          <example-card cardTitle="Migration Helper" variant="success">
            Use Lit for new components that need to work in both frameworks.
          </example-card>
        </div>

        <h5 class="font-semibold mb-2">Tailwind Lit Component (No Shadow DOM)</h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <tailwind-card card-title="Tailwind + Lit" variant="info">
            This Lit component uses global Tailwind styles!
          </tailwind-card>
          <tailwind-card card-title="Zero Conflicts" variant="success">
            Works alongside Bootstrap 3 without any style conflicts.
          </tailwind-card>
        </div>
      </div>

      <!-- Navigation -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h4 class="text-xl font-bold mb-4">Current State Info</h4>
        <div class="bg-dashboard-gray-light p-4 rounded mb-4">
          <p><strong>State Name:</strong> {{ currentState }}</p>
          <p><strong>URL:</strong> {{ currentUrl }}</p>
        </div>

        <h4 class="text-lg font-semibold mb-3">Navigation</h4>
        <p class="text-secondary mb-3">You can navigate to AngularJS routes from here:</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-default" (click)="goTo('application.programs.list')">
            Events List (AngularJS)
          </button>
          <button class="btn btn-default" (click)="goTo('application.hybrid-demo')">
            Hybrid Demo
          </button>
          <button class="btn btn-default" (click)="goBack()">
            <i class="fa fa-arrow-left"></i> Go Back
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
    :host {
      display: block;
      position: relative;
    }
    :host .ng-example-view ::ng-deep h1, :host .ng-example-view ::ng-deep h2,
    :host .ng-example-view ::ng-deep h3, :host .ng-example-view ::ng-deep h4,
    :host .ng-example-view ::ng-deep h5, :host .ng-example-view ::ng-deep h6 {
      font-weight: revert;
    }
    :host .ng-example-view ::ng-deep h4.font-semibold,
    :host .ng-example-view ::ng-deep h5.font-semibold {
      font-weight: 600;
    }
    `,
  ],
})
export class ExampleComponent implements OnInit {
  currentState = '';
  currentUrl = '';

  // For demonstrating upgraded AngularJS alert component
  alertTypes: Array<'default' | 'info' | 'warning' | 'error' | 'success'> = [
    'default',
    'info',
    'warning',
    'error',
    'success',
  ];
  alertType: 'default' | 'info' | 'warning' | 'error' | 'success' = 'info';

  setAlertType(type: 'default' | 'info' | 'warning' | 'error' | 'success'): void {
    this.alertType = type;
  }

  ngOnInit(): void {
    // Get current state from AngularJS $state service via the global injector
    try {
      const $state = (window as any).angular
        ?.element(document.body)
        ?.injector()
        ?.get('$state');
      this.currentState = $state?.current?.name || 'unknown';
    } catch {
      this.currentState = 'unknown';
    }
    this.currentUrl = window.location.pathname;
  }

  goTo(stateName: string): void {
    // Use AngularJS $state service for navigation
    try {
      const $state = (window as any).angular
        ?.element(document.body)
        ?.injector()
        ?.get('$state');
      $state?.go(stateName);
    } catch (e) {
      console.error('Failed to navigate:', e);
    }
  }

  goBack(): void {
    window.history.back();
  }
}

